import React from "react";
import { Provider as RollbarProvider } from "@rollbar/react";
import Rollbar from "rollbar/replay";
import type RollbarClient from "rollbar";

import { hasAnalyticsConsent } from "@/lib/privacyConsent";

let rollbarInstance: RollbarClient | null = null;

function clientToken(): string {
  return import.meta.env.VITE_ROLLBAR_CLIENT_TOKEN?.trim() ?? "";
}

function codeVersion(): string | undefined {
  return (
    import.meta.env.VITE_COMMIT_SHA ??
    import.meta.env.VITE_VERCEL_GIT_COMMIT_SHA
  );
}

export function isRollbarConfiguredForApp(): boolean {
  return Boolean(clientToken());
}

export function initRollbar(): RollbarClient | null {
  if (rollbarInstance) return rollbarInstance;
  if (!isRollbarConfiguredForApp() || !hasAnalyticsConsent()) return null;

  rollbarInstance = new Rollbar({
    accessToken: clientToken(),
    environment: import.meta.env.MODE,
    code_version: codeVersion(),
    captureUncaught: true,
    captureUnhandledRejections: true,
    captureIp: "anonymize",
    autoInstrument: {
      network: true,
      networkRequestBody: false,
      networkResponseBody: false,
      dom: true,
      navigation: true,
      connectivity: true,
      log: false,
    },
    scrubFields: [
      "password",
      "token",
      "access_token",
      "refresh_token",
      "authorization",
      "apikey",
      "email",
    ],
    payload: {
      client: {
        javascript: {
          source_map_enabled: true,
          guess_uncaught_frames: true,
          code_version: codeVersion(),
        },
      },
    },
    replay: {
      enabled: true,
      autoStart: true,
      triggerDefaults: {
        samplingRatio: 1,
        preDuration: 60,
        postDuration: 30,
      },
      maskInputOptions: {
        password: true,
        email: true,
        tel: true,
        text: true,
        search: true,
        url: true,
      },
      blockClass: "rollbar-block",
      maskTextClass: "rollbar-mask",
      ignoreClass: "rollbar-ignore",
    },
  });

  return rollbarInstance;
}

export function getRollbar(): RollbarClient | null {
  return rollbarInstance ?? initRollbar();
}

export function setRollbarUser(user: { id: string } | null): void {
  const rollbar = getRollbar();
  if (!rollbar) return;

  rollbar.configure({
    payload: {
      person: user ? { id: user.id } : { id: null },
    },
  });
}

export function triggerRollbarReplay(context: Record<string, unknown>): void {
  getRollbar()?.triggerDirectReplay(context);
}

export function RollbarRoot({ children }: { children: React.ReactNode }) {
  const rollbar = React.useMemo(() => initRollbar(), []);

  if (!rollbar) {
    return <>{children}</>;
  }

  return <RollbarProvider instance={rollbar}>{children}</RollbarProvider>;
}
